// Melody: two motifs per track (the second made from the first), restated per chord inside 4-bar phrases (motif 2 bars ·
// bar 3 · answer 1 bar) and developed as the track goes on, plus the improvised lines of a solo (docs/AUDIO_DESIGN.md §6).
import { scaleNotes } from './harmony.js';

const CELLS = [[0, 3, 6, 8], [2, 4, 7], [0, 6, 10, 12, 14], [0, 2, 6, 8], [3, 6, 10], [0, 4, 6, 10], [2, 6, 10, 12], [0, 3, 8, 10],
  [0, 2, 4, 7], [1, 4, 8], [0, 8, 10, 14], [4, 6, 8, 12], [0, 6, 8, 11], [2, 5, 8], [0, 1, 4, 10], [6, 8, 10]];
const TAILS = [[], [16], [16, 19], [18], [16, 22], [20], [24], [16, 18, 20], [22, 24], [19, 22]];   // optional notes in the motif's 2nd bar
const ANSWERS = [[0, 4, 8], [2, 6, 10], [0, 3, 6, 10], [4, 8], [0, 6, 12], [2, 4, 8, 12]];
const LO = 67, HI = 84;
const mod12 = n => ((n % 12) + 12) % 12;

/** Scale-step contour: moves of <= 2 steps (p .75) or a leap of up to a 5th followed by a step back. */
function contour(r, n) {
  const c = [0];
  let dir = r() < 0.5 ? 1 : -1, back = 0;
  for (let i = 1; i < n; i++) {
    let mv;
    if (back) { mv = -back; back = 0; }
    else if (r() < 0.75) { mv = (1 + Math.floor(r() * 2)) * (r() < 0.65 ? dir : -dir); }
    else { mv = (2 + Math.floor(r() * 2)) * dir; back = Math.sign(mv); }
    c.push(c[i - 1] + mv);
    if (Math.abs(c[i]) > 4) { dir = -Math.sign(c[i]); }
  }
  return c;
}

/** One motif per track: 3-6 notes over 2 bars (16th steps 0..31); the last note lasts 4-8 steps. */
export function makeMotif(r, key) {
  let steps = CELLS[Math.floor(r() * CELLS.length)].slice();
  steps = steps.concat(TAILS[Math.floor(r() * TAILS.length)]).slice(0, 6);
  while (steps.length < 3) steps.push(steps[steps.length - 1] + 4);
  const n = steps.length, last = 4 + Math.floor(r() * 5);
  const durs = steps.map((s, i) => (i < n - 1 ? Math.min(6, steps[i + 1] - s) : last));
  return { steps, durs, contour: contour(r, n), vel: steps.map((s, i) => (i === 0 ? 0.78 : 0.62 + 0.1 * r())), mode: key.mode };
}

/**
 * The track's second motif (its B sections and the bridge), made from the first so it keeps the style's rhythm and line
 * (every style's motif has the same shape): the rhythm mirrored inside its two bars (p .5) or moved 2, 4 or 6 steps on
 * (the notes pushed past the end wrap to the front), the contour upside down (p .6) or as it falls out of that.
 */
export function deriveMotif(m, r) {
  const n = m.steps.length, end = Math.min(32, Math.max(...m.steps.map((s, i) => s + m.durs[i])));
  let notes = m.steps.map((s, i) => ({ s, d: m.durs[i], c: m.contour[i] }));
  if (r() < 0.5) notes = notes.map(x => ({ ...x, s: Math.max(0, end - x.s - x.d) })).reverse();
  else { const k = 2 * (1 + Math.floor(r() * 3)); notes = notes.map(x => ({ ...x, s: (x.s + k) % 32 })).sort((a, b) => a.s - b.s); }
  for (let i = 1; i < n; i++) if (notes[i].s <= notes[i - 1].s) notes[i].s = notes[i - 1].s + 1;
  notes = notes.filter(x => x.s <= 31);
  const flip = r() < 0.6, c0 = notes[0].c;
  return {
    steps: notes.map(x => x.s),
    durs: notes.map((x, i) => (i < notes.length - 1 ? Math.max(1, Math.min(x.d, notes[i + 1].s - x.s)) : Math.max(x.d, m.durs[n - 1]))),
    contour: notes.map(x => (flip ? c0 - x.c : x.c - c0)),
    vel: notes.map((x, i) => m.vel[Math.min(i, n - 1)]),
    mode: m.mode,
  };
}

/** Scale helpers over `scale` (MIDI notes of the pentatonic) for a phrase whose chord at step s is chordAt(s). */
function tools(scale, chordAt) {
  const inRange = i => scale[i] >= LO && scale[i] <= HI;
  const nearest = (target, pcs) => {
    if (!Number.isFinite(target)) target = 74;
    let best = -1, bd = Infinity;
    for (let i = 0; i < scale.length; i++) {
      if (!inRange(i) || (pcs && !pcs.includes(mod12(scale[i])))) continue;
      const d = Math.abs(scale[i] - target);
      if (d < bd) { bd = d; best = i; }
    }
    if (best >= 0) return best;
    return pcs ? nearest(target, null) : Math.max(0, scale.findIndex(m => m >= LO));
  };
  const chordTone = (i, s) => {                     // snap scale index i to a chord tone that is in the scale
    const pcs = chordAt(s);
    if (pcs.includes(mod12(scale[i]))) return i;
    for (const d of [1, -1, 2, -2]) if (scale[i + d] != null && inRange(i + d) && pcs.includes(mod12(scale[i + d]))) return i + d;
    return i;
  };
  const clampIdx = i => {
    i = Math.max(0, Math.min(scale.length - 1, Math.round(i) || 0));
    while (i > 0 && scale[i] > HI) i--;
    while (i < scale.length - 1 && scale[i] < LO) i++;
    return i;
  };
  /** a phrase spans <= span semitones: fold outliers towards the lowest note; → [{ s, d, midi, v }] by time */
  const fold = (notes, span) => {
    const lo = Math.min(...notes.map(n => scale[n.i]));
    return notes.map(n => {
      let m = scale[n.i];
      while (m > lo + span) m -= 12;                // lo >= LO, so this stays in range and in the scale
      if (scale.indexOf(m) < 0) m = scale[nearest(m, null)];
      return { s: n.s, d: n.d, midi: m, v: n.v };
    }).sort((a, b) => a.s - b.s);
  };
  return { nearest, chordTone, clampIdx, fold };
}

/**
 * Notes of one 4-bar phrase. chordAt(step 0..63) → pitch classes of the chord sounding there.
 * variant: { statement (1-based), inv (the bridge: inverted contour), shift (steps), prevLast (MIDI), register (semitones:
 *   without a prevLast, the phrase starts around 74 + register, the track's own register),
 *   treat: 'state' (as written) | 'seq' (moved `by` scale steps) | 'displace' (moved `by` steps later) | 'frag' (its
 *   first half, then again a bar on and a step up), form: what bar 3 does, 'rest' | 'echo' (the motif's tail again, a
 *   step away) | 'run' (a walk on from it) }
 * → [{ s (0..63), d (steps), midi, v }]
 */
export function phrase(motif, chordAt, key, r, variant = {}) {
  const scale = scaleNotes(key, 55, 96), { nearest, chordTone, clampIdx, fold } = tools(scale, chordAt);
  const { treat = 'state', by = 0, form = 'rest' } = variant;
  const notes = [];
  const shift = (variant.shift || 0) + (treat === 'displace' ? by : 0), cont = variant.inv ? motif.contour.map(c => -c) : motif.contour;
  let anchor = nearest((variant.prevLast ?? (74 + (variant.register || 0))) + (r() * 4 - 2), chordAt(shift));
  if (treat === 'seq') anchor = clampIdx(anchor + by);
  let src = motif.steps.map((s0, i) => ({ s: s0 + shift, d: motif.durs[i], c: cont[i], v: motif.vel[i] })).filter(x => x.s <= 31);
  if (treat === 'frag') {
    const head = src.slice(0, Math.max(2, Math.ceil(src.length / 2))).filter(x => x.s < 16);
    if (head.length >= 2) src = [...head.map(x => ({ ...x, d: Math.min(x.d, 16 - x.s) })), ...head.map(x => ({ ...x, s: x.s + 16, c: x.c + 1, v: x.v * 0.95 }))];
  }
  for (const x of src) {
    let idx = clampIdx(anchor + x.c);
    if (x.s % 16 === 0 || x.s % 16 === 8) idx = chordTone(idx, x.s);
    notes.push({ s: x.s, d: x.d, i: idx, v: x.v });
  }
  if (variant.statement && variant.statement % 3 === 0 && notes.length > 1) {   // the 3rd statement changes its ending
    const last = notes[notes.length - 1];
    last.i = clampIdx(last.i + (r() < 0.5 ? 2 : -1));
    last.d = Math.max(2, last.d - 2);
    notes.push({ s: Math.min(31, last.s + last.d + 1), d: 4, i: clampIdx(chordTone(last.i - 1, 30)), v: 0.6 });
  }
  // a short walk from the last note over one of the answer cells, starting at step `at`
  const walk = (at, cell, end, chordEnd) => {
    let cur = notes.length ? notes[notes.length - 1].i : anchor;
    cell.forEach((c, k) => {
      const s = at + c;
      const mv = r() < 0.75 ? (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 2)) : (r() < 0.5 ? -3 : 3);
      cur = clampIdx(cur + mv);
      if ((chordEnd && k === cell.length - 1) || s % 16 === 0 || s % 16 === 8) cur = chordTone(cur, s);
      const d = k < cell.length - 1 ? Math.min(6, cell[k + 1] - c) : Math.min(end - s, 4 + Math.floor(r() * 5));
      notes.push({ s, d: Math.max(1, d), i: cur, v: 0.58 + 0.12 * r() });
    });
  };
  // bar 3 (steps 32..47): a rest, the motif's tail echoed a step away, or a walk on
  if (form === 'echo' && notes.length) {
    const tail = notes.slice(-Math.min(3, notes.length)), s0 = tail[0].s, off = 32 + (s0 % 8) - s0, mv = r() < 0.5 ? 1 : -1;
    for (const t of tail) {
      const s = t.s + off;
      if (s <= 45) notes.push({ s, d: Math.max(1, Math.min(t.d, 47 - s)), i: clampIdx(t.i + mv), v: t.v * 0.9 });
    }
  } else if (form === 'run') walk(32, ANSWERS[Math.floor(r() * ANSWERS.length)], 47, false);
  // answer bar (steps 48..63): a short walk from the last note, ending on a chord tone
  walk(48, ANSWERS[Math.floor(r() * ANSWERS.length)], 63, true);
  return fold(notes, 12);
}

/**
 * One 4-bar phrase of a solo: two lines (bar 1 into bar 2, bar 3 into bar 4), a note on each `unit`-step slot with odds
 * `busy` (a line's first always), walking the scale by steps with a leap now and then, rising through the first half of
 * a line and falling through the second; downbeats, half-bars and each line's last note land on chord tones, and the
 * last one is held into the breath before the next line. Starts near prevLast (or 74 + register).
 * → [{ s (0..63), d (steps), midi, v }]
 */
export function improv(chordAt, key, r, { prevLast = null, register = 0, unit = 2, busy = 0.55 } = {}) {
  const scale = scaleNotes(key, 55, 96), { nearest, chordTone, clampIdx, fold } = tools(scale, chordAt);
  let cur = nearest((prevLast ?? (74 + register)) + (r() * 4 - 2), chordAt(0));
  const notes = [];
  for (const [a, z] of [[0, 26], [32, 58]]) {
    const on = [];
    for (let s = a; s < z; s += unit) if (s === a || r() < busy) on.push(s);
    on.forEach((s, k) => {
      const up = s < (a + z) / 2 ? 1 : -1, u = r();
      if (k) cur = clampIdx(cur + (u < 0.6 ? up : u < 0.8 ? -up : u < 0.93 ? 2 * up : 3 * up));
      if (s % 8 === 0 || k === on.length - 1) cur = chordTone(cur, s);
      const next = on[k + 1], d = next == null ? z + 2 - s : Math.max(1, next - s - (r() < 0.4 ? 1 : 0));
      notes.push({ s, d, i: cur, v: +(0.6 + 0.1 * r() + (s % 8 === 0 ? 0.06 : 0)).toFixed(3) });
    });
  }
  return fold(notes, 15);
}
